import { FC, useCallback, useEffect, useRef } from 'react';
import './retry-message-input.scss';

const PLACEHOLDER_RE = /\{Message\}/g;
const PLACEHOLDER_COLOR = 'var(--ps-color-brand, #8573e7)';

const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const highlightPlaceholders = (text: string): string =>
  escapeHtml(text).replace(
    PLACEHOLDER_RE,
    `<span style="color:${PLACEHOLDER_COLOR};pointer-events:none">{Message}</span>`
  );

interface CaretRange {
  start: number;
  end: number;
}

const getCaretRange = (el: HTMLElement): CaretRange => {
  const sel = window.getSelection();
  if (!sel?.rangeCount) return { start: 0, end: 0 };

  const selRange = sel.getRangeAt(0);
  const startRange = document.createRange();
  startRange.selectNodeContents(el);
  startRange.setEnd(selRange.startContainer, selRange.startOffset);
  const start = startRange.toString().length;

  const endRange = document.createRange();
  endRange.selectNodeContents(el);
  endRange.setEnd(selRange.endContainer, selRange.endOffset);
  const end = endRange.toString().length;

  return { start, end };
};

const setCaretRange = (el: HTMLElement, { start, end }: CaretRange): void => {
  const resolveNode = (
    offset: number
  ): { node: Text; offset: number } | null => {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let remaining = offset;
    let node = walker.nextNode() as Text | null;
    while (node) {
      if (remaining <= node.length) return { node, offset: remaining };
      remaining -= node.length;
      node = walker.nextNode() as Text | null;
    }
    return null;
  };

  const startPos = resolveNode(start);
  const endPos = resolveNode(end);
  if (!startPos || !endPos) return;

  const range = document.createRange();
  range.setStart(startPos.node, startPos.offset);
  range.setEnd(endPos.node, endPos.offset);

  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
};

interface RetryMessageInputProps {
  value: string;
  onChange: (value: string) => void;
}

export const RetryMessageInput: FC<RetryMessageInputProps> = ({
  value,
  onChange,
}) => {
  const divRef = useRef<HTMLDivElement>(null);
  const isFocusedRef = useRef(false);
  const isComposingRef = useRef(false);

  // Initialize DOM on mount — React must not own this element's content
  useEffect(() => {
    if (divRef.current) divRef.current.innerHTML = highlightPlaceholders(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync external resets (e.g. toggle off/on) only when the field is not focused
  useEffect(() => {
    if (!divRef.current || isFocusedRef.current) return;
    if ((divRef.current.textContent ?? '') !== value) {
      divRef.current.innerHTML = highlightPlaceholders(value);
    }
  }, [value]);

  const applyHighlight = useCallback(() => {
    const el = divRef.current;
    if (!el) return;
    const caretRange = getCaretRange(el);
    el.innerHTML = highlightPlaceholders(el.textContent ?? '');
    setCaretRange(el, caretRange);
  }, []);

  const handleInput = useCallback(() => {
    if (isComposingRef.current) return;
    applyHighlight();
    onChange(divRef.current?.textContent ?? '');
  }, [applyHighlight, onChange]);

  return (
    <div
      ref={divRef}
      contentEditable
      suppressContentEditableWarning
      className="c-retry-message-input"
      data-testid="ps-voice-channel-retry-message-input"
      onFocus={(e) => {
        isFocusedRef.current = true;
        e.currentTarget.style.borderColor = PLACEHOLDER_COLOR;
      }}
      onBlur={(e) => {
        isFocusedRef.current = false;
        e.currentTarget.style.borderColor = '';
      }}
      onInput={handleInput}
      onCompositionStart={() => {
        isComposingRef.current = true;
      }}
      onCompositionEnd={() => {
        isComposingRef.current = false;
        handleInput();
      }}
    />
  );
};
